import { mapState } from 'pinia'

import RichContent from 'src/components/rich_content/rich_content.jsx'

import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useUsersStore } from 'src/stores/users.js'

import {
  quoteUrls,
  withoutQuoteFallback,
} from 'src/services/quoting/quoting.js'

// The bottom half of the fade over a cut post's text (8em, see the
// stylesheet), where the text is faint: y is a tap, bottom the text's.
export const inFaintFade = ({ y, bottom, em }) => y > bottom - 4 * em

const StatusBody = {
  name: 'StatusBody',
  components: {
    RichContent,
  },
  props: {
    // see RichContent
    replyArrow: { type: Object, default: null },
    // the quote shows as a card right after: the text's "RE: <link>" goes
    withQuote: Boolean,
    status: {
      // Main thing
      type: Object,
      required: true,
    },
    compact: {
      // Resizes emoji and minimizes vertical space used
      // Primarily used for showing status in react notifications
      type: Boolean,
      default: false,
    },
    collapse: {
      // replaces newlines with spaces
      type: Boolean,
      default: false,
    },
    singleLine: {
      // Show entire thing (subject and content) in a single line
      // Primarily used in chats
      type: Boolean,
      default: false,
    },
    inConversation: {
      // Is status rendered within open conversation?
      // Used to automatically expand subjects (if collapsed)
      type: Boolean,
      default: false,
    },
    ignoreSubject: {
      // Pretend subject line doesn't exist. Useful for chat messages
      // to indicate what post reply belongs to
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      postLength: this.status.text.length,
      parseReadyDone: false,
      showingTall: false,
      showingLongSubject: false,
      expandingSubject: null,
      // the end of the open text is out of sight: the close control sticks
      // to the bottom of the screen over the text
      stuck: false,
    }
  },
  emits: ['parseReady'],
  computed: {
    // the text, without the "RE: <link>" of a quote shown as a card
    html() {
      if (!this.withQuote) return this.status.raw_html
      return withoutQuoteFallback(this.status.raw_html, quoteUrls(this.status))
    },
    // This is a bit hacky, but we want to approximate post height before rendering
    // so we count newlines (masto uses <p> for paragraphs, GS uses <br> between them)
    // as well as approximate line count by counting characters and approximating ~80
    // per line.
    //
    // Using max-height + overflow: auto for status components resulted in false positives
    // very often with japanese characters, and it was very annoying.
    hasLongSubject() {
      return this.status.summary.length > 240
    },
    hasSubject() {
      return !!this.status.summary && !this.ignoreSubject
    },
    // When a status has a subject and is also tall, we should only have one show more/less
    // button. If the default is to collapse statuses with subjects, we just treat it like
    // a status with a subject; otherwise, we just treat it like a tall status.
    mightHideBecauseSubject() {
      return (
        !this.inConversation &&
        this.hasSubject &&
        this.mergedConfig.collapseMessageWithSubject
      )
    },
    mightHideBecauseTall() {
      if (this.singleLine || this.compact) return false
      const lengthScore =
        this.status.raw_html.split(/<p|<br/).length + this.postLength / 80
      return lengthScore > 20
    },
    hideSubjectStatus() {
      return this.mightHideBecauseSubject && !this.expandingSubject
    },
    hideTallStatus() {
      return this.mightHideBecauseTall && !this.showingTall
    },
    shouldShowExpandToggle() {
      return this.mightHideBecauseSubject || this.mightHideBecauseTall
    },
    // Cut short, not hidden behind a content warning: a tap on a touch
    // screen opens it.
    cut() {
      return (
        !this.compact &&
        !this.singleLine &&
        !this.shouldHide &&
        ((this.hasLongSubject && !this.showingLongSubject) ||
          this.hideTallStatus)
      )
    },
    toggleButtonClasses() {
      return {
        // a tall post's link, not a content warning's button
        '-more': !this.mightHideBecauseSubject,
        'cw-status-hider': !this.showingMore && this.mightHideBecauseSubject,
        'tall-status-hider': !this.showingMore && this.mightHideBecauseTall,
        'status-unhider': this.showingMore,
        '-stuck': this.showingMore && this.stuck,
      }
    },
    toggleText() {
      if (this.showingMore) {
        return this.mightHideBecauseSubject
          ? this.$t('status.hide_content')
          : this.$t('general.show_less')
      } else {
        return this.mightHideBecauseSubject
          ? this.$t('status.show_content')
          : this.$t('general.show_more')
      }
    },
    shouldHide() {
      return (
        !this.showingMore && this.mightHideBecauseSubject && this.hasSubject
      )
    },
    showingMore() {
      return (
        (this.mightHideBecauseTall && this.showingTall) ||
        (this.mightHideBecauseSubject && this.expandingSubject)
      )
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
  },
  mounted() {
    this.status.attentions?.forEach((attn) => {
      const { id } = attn
      useUsersStore().fetchUserIfMissing({ id })
    })
  },
  methods: {
    // a function ref on the end of the open text: watched while it is there
    // (Vue calls it on each render too)
    watchEnd(el) {
      if (el === this.endEl) return
      this.endEl = el
      this.endObserver?.disconnect()
      this.endObserver = null
      this.stuck = false
      if (!el) return
      this.endObserver = new IntersectionObserver(([entry]) => {
        this.stuck = !entry.isIntersecting
      })
      this.endObserver.observe(el)
    },
    onParseReady(event) {
      if (this.parseReadyDone) return
      this.parseReadyDone = true
      this.$emit('parseReady', event)
      const { writtenMentions, invisibleMentions } = event
      writtenMentions
        .filter((mention) => !mention.notifying)
        .forEach((mention) => {
          const { content, url } = mention
          const cleanedString = content.replaceAll(/<[^>]+?>/gi, '') // remove all tags
          if (!cleanedString.startsWith('@')) return
          const handle = cleanedString.slice(1)
          const host = url.replace(/^https?:\/\//, '').replace(/\/.+?$/, '')
          useUsersStore().fetchUserIfMissing({ name: `${handle}@${host}` })
        })
      /* This is a bit of a hack to make current tall status detector work
       * with rich mentions. Invisible mentions are detected at RichContent level
       * and also we generate plaintext version of mentions by stripping tags
       * so here we subtract from post length by each mention that became invisible
       * via MentionsLine
       */
      this.postLength = invisibleMentions.reduce((acc, mention) => {
        return acc - mention.textContent.length - 1
      }, this.postLength)
    },
    toggleShowingLongSubject() {
      this.showingLongSubject = !this.showingLongSubject
    },
    // One button for a tall post opens and closes the subject with it.
    toggleShowMore() {
      if (this.mightHideBecauseTall) {
        this.showingTall = !this.showingTall
        this.showingLongSubject = this.showingTall
      } else if (this.mightHideBecauseSubject) {
        this.expandingSubject = !this.expandingSubject
      }
    },
    // On a touch screen a tap on the text opens subject and text for good,
    // besides the "Show more" link (a long subject has no button there).
    // Links, buttons and media keep their own taps.
    // a tap that ends a selection is not a tap on the post
    selectingText() {
      const selection = document.getSelection()
      return !selection.isCollapsed && this.$el.contains(selection.anchorNode)
    },
    // On a touch screen a tap on a link that is faint in the fade opens the
    // post: the link is hard to see, so it was hit by chance. Before the
    // link's own handlers, which would follow it.
    onFadeTap(event) {
      const link = event.target.closest?.('a')
      if (!link || !this.hideTallStatus) return
      if (!window.matchMedia?.('(pointer: coarse)').matches) return
      const text = this.$refs.text?.$el
      if (!text) return
      const { bottom } = text.getBoundingClientRect()
      const em = parseFloat(getComputedStyle(text).fontSize)
      if (!inFaintFade({ y: event.clientY, bottom, em })) return
      event.preventDefault()
      event.stopPropagation()
      this.showingTall = true
      this.showingLongSubject = true
    },
    onBodyTap(event) {
      if (!this.cut || !window.matchMedia?.('(pointer: coarse)').matches) {
        return
      }
      if (event.target.closest?.(INTERACTIVE) || this.selectingText()) return
      this.showingTall = true
      this.showingLongSubject = true
    },
  },
}

const INTERACTIVE =
  'a, button, input, textarea, select, label, video, audio, [role="button"]'

export default StatusBody
